import React from 'react';
import PropTypes from 'prop-types';

import Moment from 'moment';
import { extendMoment } from 'moment-range';

import { Field, reduxForm } from 'redux-form';

import { withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import IconButton from 'components/common/IconButton';
import Button from 'components/common/Button';
import IconArrowHeadLeft from 'components/icons/IconArrowHeadLeft';
import IconArrowHeadRight from 'components/icons/IconArrowHeadRight';
import AutosuggestField from 'components/fields/AutosuggestField';

import pxToRem from 'styles/pxToRem';

import * as validate from 'utils/validate';

const moment = extendMoment(Moment);

const styles = theme => ({
  root: {
    padding: '24px 24px 0 24px',
  },
  buttons: {
    margin: '-2px -2px 0 -2px',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    '& + $navigation': {
      marginTop: 4,
    },
  },
  button: {
    minWidth: 77,
    display: 'inline-flex',
    margin: 2,
    textTransform: 'none',
    boxShadow: 'none',
    fontSize: pxToRem(13),
    letterSpacing: '0.5px',
    paddingTop: 4,
    paddingBottom: 4,
    paddingLeft: 8,
    paddingRight: 8,
    minHeight: 24,
    '&:after': {
      borderColor: theme.palette.grey[600],
    },
  },
  navigation: {
    marginTop: -8,
    marginBottom: -8,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  monthsNavigation: {
    minWidth: 180,
    display: 'inline-flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  monthName: {
    fontSize: pxToRem(13),
    marginLeft: -10,
    marginRight: -10,
    textAlign: 'center',
  },
  iconArrowHead: {
    width: 7.4,
    height: 12,
    color: theme.palette.grey[600],
  },
  yearsNavigation: {
    width: 103,
  },
});

function onSubmit(values, dispatch, props) {
  props.onMonthChange(props.currentMonth.clone().set('year', values.year));
  return values;
}

function onChange(values, dispatch, props, prevValues) {
  if (props.dirty
    && values.year
    && values.year !== prevValues.year
    && values.year !== props.initialValues.year
    && moment(values.year, 'YYYY', true).isValid()) {
    const newCurrentMonth = props.currentMonth.clone().set('year', values.year);
    if (!newCurrentMonth.isAfter(props.maxDate.endOf('month'))
      && !newCurrentMonth.isBefore(props.minDate.startOf('month'))) {
      setTimeout(() => {
        props.submit();
      }, 100);
    }
  }
}

function validateForm(values, props) {
  const errors = {};

  if (values.year) {
    if (!moment(values.year, 'YYYY', true).isValid()) {
      errors.year = 'Invalid year';
    } else if (props.currentMonth.clone().set('year', values.year)
      .isAfter(props.maxDate.endOf('month'))) {
      errors.year = 'Year is after maximum';
    } else if (props.currentMonth.clone().set('year', values.year)
      .isBefore(props.minDate.startOf('month'))) {
      errors.year = 'Year is before minimum';
    }
  }

  return errors;
}

const DatepickerControlsForm = (props) => {
  const {
    classes,
    currentMonth,
    onMonthChange,
    minDate,
    maxDate,
    disableTabFocus,
    rangeButtons,
    onRangeButtonClick,
  } = props;

  const prevMonthButtonDisabled = currentMonth.clone()
    .subtract(1, 'month').isBefore(minDate.startOf('month'));
  const nextMonthButtonDisabled = currentMonth.clone()
    .add(1, 'month').isAfter(maxDate.startOf('month'));
  const onPrevMonthButtonClick = () => onMonthChange(currentMonth.clone()
    .subtract(1, 'month'));
  const onNextMonthButtonClick = () => onMonthChange(currentMonth.clone()
    .add(1, 'month'));
  const years = Array
    .from(moment.range(minDate.clone().startOf('year'), maxDate.clone().endOf('year')).by('year'))
    .map(item => item.format('YYYY'));
  const reverseYearsOnClear = maxDate.clone().endOf('year').isSame(moment().endOf('year'));
  return (
    <div className={classes.root}>
      {rangeButtons &&
        <div className={classes.buttons}>
          <Button
            className={classes.button}
            size="sm"
            variant="raised"
            onClick={() => {
              onRangeButtonClick('today');
            }}
          >
            Today
          </Button>
          <Button
            className={classes.button}
            size="sm"
            variant="raised"
            onClick={() => {
              onRangeButtonClick('this week');
            }}
          >
            This Week
          </Button>
          <Button
            className={classes.button}
            size="sm"
            variant="raised"
            onClick={() => {
              onRangeButtonClick('this month');
            }}
          >
            This Month
          </Button>
          <Button
            className={classes.button}
            size="sm"
            variant="raised"
            onClick={() => {
              onRangeButtonClick('yesterday');
            }}
          >
            Yesterday
          </Button>
          <Button
            className={classes.button}
            size="sm"
            variant="raised"
            onClick={() => {
              onRangeButtonClick('last week');
            }}
          >
            Last Week
          </Button>
          <Button
            className={classes.button}
            size="sm"
            variant="raised"
            onClick={() => {
              onRangeButtonClick('last month');
            }}
          >
            Last Month
          </Button>
        </div>
      }
      <div className={classes.navigation}>
        <nav className={classes.monthsNavigation}>
          <IconButton
            onClick={onPrevMonthButtonClick}
            disabled={prevMonthButtonDisabled}
            tooltipContent="Previous Month"
            {...disableTabFocus ? { tabIndex: -1 } : {}}
          >
            <IconArrowHeadLeft className={classes.iconArrowHead} />
          </IconButton>
          <Typography className={classes.monthName}>
            {currentMonth.format('MMMM YYYY')}
          </Typography>
          <IconButton
            onClick={onNextMonthButtonClick}
            disabled={nextMonthButtonDisabled}
            tooltipContent="Next Month"
            {...disableTabFocus ? { tabIndex: -1 } : {}}
          >
            <IconArrowHeadRight className={classes.iconArrowHead} />
          </IconButton>
        </nav>
        <div className={classes.yearsNavigation}>
          <Field
            id="year"
            name="year"
            component={AutosuggestField}
            validate={[validate.required]}
            placeholder="-Year-"
            fullWidth
            listWidth="auto"
            size="xs"
            renderSuggestionContent={suggestion => suggestion}
            onSuggestionSelected={suggestion => suggestion}
            {...disableTabFocus ? { tabIndex: -1 } : {}}
            fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
              if (offset === 0) {
                if (value.length > 0) {
                  resolve(years
                    .filter(item => item.toLowerCase().indexOf(value.toLowerCase()) !== -1));
                } else if (reverseYearsOnClear) {
                  resolve(years.reverse());
                } else {
                  resolve(years);
                }
              } else {
                resolve([]);
              }
            }))}
          />
        </div>
      </div>
    </div>
  );
};

DatepickerControlsForm.propTypes = {
  currentMonth: PropTypes.instanceOf(Moment).isRequired,
  onMonthChange: PropTypes.func.isRequired,
  onRangeButtonClick: PropTypes.func.isRequired,
  minDate: PropTypes.instanceOf(Moment).isRequired,
  maxDate: PropTypes.instanceOf(Moment).isRequired,
  disableTabFocus: PropTypes.bool.isRequired,
  rangeButtons: PropTypes.bool.isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    buttons: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
    navigation: PropTypes.string.isRequired,
    monthsNavigation: PropTypes.string.isRequired,
    monthName: PropTypes.string.isRequired,
    iconArrowHead: PropTypes.string.isRequired,
    yearsNavigation: PropTypes.string.isRequired,
  }).isRequired,
};

export default reduxForm({
  onSubmit,
  onChange,
  validate: validateForm,
  enableReinitialize: true,
})(withStyles(styles)(DatepickerControlsForm));



// WEBPACK FOOTER //
// ./src/components/fields/DatepickerField/DatepickerControlsForm.js